import type { Module, ViewRestrictedModule } from '@felix/domain';
import {
  createReactComponent,
  IconBuildingWarehouse,
  IconCash,
  IconClipboardCheck,
  IconFileText,
  IconGasStation,
  IconHome,
  IconPackage,
  IconReport,
  IconSettingsFilled,
  IconShip,
  IconShoppingCart,
  IconSteeringWheel,
  IconTool,
  type TablerIcon,
} from '@tabler/icons-react';

/**
 * Dolu "mürettebat" ikonu (personel-*.jpeg menüsünde Crew dolu iki kişi); Tabler'de dolu çok kişi
 * ikonu yok. 24×24 ızgarada iki siluet; renk `currentColor` (M6f G1-17).
 */
const IconCrewFilled: TablerIcon = createReactComponent('filled', 'crew-filled', 'CrewFilled', [
  ['path', { d: 'M9 4a3.5 3.5 0 1 1 0 7a3.5 3.5 0 0 1 0 -7z', key: 'svg-0' }],
  [
    'path',
    {
      d: 'M7 13h4a5 5 0 0 1 5 5v1.5a1.5 1.5 0 0 1 -1.5 1.5h-11a1.5 1.5 0 0 1 -1.5 -1.5v-1.5a5 5 0 0 1 5 -5z',
      key: 'svg-1',
    },
  ],
  ['path', { d: 'M17 5a3 3 0 1 1 0 6a3 3 0 0 1 0 -6z', key: 'svg-2' }],
  [
    'path',
    {
      d: 'M17.5 13h.5a4 4 0 0 1 4 4v2.5a1.5 1.5 0 0 1 -1.5 1.5h-2.9a3.5 3.5 0 0 0 .4 -1.5v-1.5a6.5 6.5 0 0 0 -2 -4.7c.5 -.2 1 -.3 1.5 -.3z',
      key: 'svg-3',
    },
  ],
]);

/** Menüyü kullanan uygulama: web ofis ya da gemi uygulaması (aynı menü, farklı kapsam). */
export type MenuScope = 'web' | 'ship';

/**
 * Henüz yapılmamış öğenin geleceği aşama (`docs/yol-haritasi.md`). Metni `stages.<kod>`.
 * `nextUpdate`: ekran var, bu uygulamaya bir sonraki sürümde gelir.
 */
export type MenuStage =
  | 'M7'
  | 'M8a'
  | 'M8b'
  | 'M10d'
  | 'M11'
  | 'M12a'
  | 'M12b'
  | 'M12c'
  | 'phase2'
  | 'phase3'
  /** Sefer olayları, özeti ve zaman çizgisi (ortak 2026-10-18 planı V2; ADR 0082). */
  | 'V2'
  /** Yakıt ve tüketim analizi (ortak 2026-10-18 planı V6; ADR 0082). */
  | 'V6'
  | 'withModules'
  | 'nextUpdate'
  /** Ekran yalnız ofiste (web) kullanılır; gemide bilgi sayfası (ör. tedarikçi; ADR 0026 §1). */
  | 'office';

export interface MenuItem {
  /** Kimlik; adı `menu.<id>`, "hazırlanıyor" açıklaması `comingSoon.modules.<id>`. */
  id: string;
  /** Alt öğesi olan grupta ilk alt öğenin yolu (breadcrumb bağlantısı). */
  path: string;
  /** Yalnız ana öğelerde. */
  icon?: TablerIcon;
  /** `null`: hazır. */
  stage: MenuStage | null;
  /** Gemi uygulamasında aşama farklıysa. */
  shipStage?: MenuStage | null;
  /**
   * Görme izni gereken kısıtlı modül (ADR 0061 §6): kişi bu modülü göremiyorsa öğe (grupsa bütün
   * alt öğeleriyle) menüde gizlenir.
   */
  permission?: ViewRestrictedModule;
  children?: readonly MenuItem[];
  /**
   * Menüde satır olarak görünmeyen yol (ADR 0082; menu-yapisi.md "Menüde görünmeyen yollar"):
   * sayfa açılır, iz ve başlık bu öğeden gelir; sol menüde `activeAs` öğesi etkin görünür.
   */
  offMenu?: { activeAs: string };
  /**
   * Sayfa alt yolları kendi sekmeleriyle açar (Fuel Management `/fuel/<sekme>`; ADR 0117 §14):
   * uygulamalar yolu `<path>/*` olarak kurar; iz ve etkin satır bu öğedir.
   */
  nested?: boolean;
}

function leaf(
  id: string,
  path: string,
  stage: MenuStage | null,
  shipStage?: MenuStage | null,
): MenuItem {
  return shipStage === undefined ? { id, path, stage } : { id, path, stage, shipStage };
}

function group(id: string, icon: TablerIcon | null, children: readonly MenuItem[]): MenuItem {
  const first = children[0];
  if (!first) throw new Error(`boş menü grubu: ${id}`);
  return icon
    ? { id, path: first.path, icon, stage: null, children }
    : { id, path: first.path, stage: null, children };
}

/** Menüde görünmeyen, yolu yaşayan sayfa (ADR 0082); sol menüde `activeAs` etkin görünür. */
function offMenu(id: string, path: string, activeAs: string): MenuItem {
  return { id, path, stage: null, offMenu: { activeAs } };
}

/** Üçüncü düzey grup (ikonsuz; menu-yapisi.md "ISM Forms ▸ Form List"). */
function subgroup(id: string, children: readonly MenuItem[]): MenuItem {
  return group(id, null, children);
}

/**
 * Sol menü: gemi uygulaması ve web ofis için tek kaynak (ADR 0019 §2). Sıra, adlar ve aşamalar
 * `docs/ekranlar/menu-yapisi.md` tablosundan; `menu.test.ts` belgeyle aynı olduğunu doğrular.
 */
export const MENU: readonly MenuItem[] = [
  { id: 'dashboard', path: '/dashboard', icon: IconHome, stage: null },
  group('fleet', IconShip, [leaf('fleetVessels', '/fleet', null)]),
  // Ortağın 2026-10-18 önerisi (ADR 0082; sefer-1, sefer-6): dört alt grup. Rapor öğeleri All
  // Reports'u tür süzgeciyle açar; Leg / Event ve Noon formu menüde görünmez ama yolları yaşar
  group('voyage', IconReport, [
    subgroup('voyageReports', [
      leaf('voyageReportsAll', '/voyage/reports', null),
      leaf('voyageReportsArrival', '/voyage/reports/arrival', null),
      leaf('voyageReportsDailyPort', '/voyage/reports/daily-port', null),
      leaf('voyageReportsDeparture', '/voyage/reports/departure', null),
      leaf('voyageReportsAnchoring', '/voyage/reports/anchoring', null),
      leaf('voyageReportsAnchorAweigh', '/voyage/reports/anchor-aweigh', null),
      leaf('voyageReportsDrifting', '/voyage/reports/drifting', null),
      leaf('voyageReportsDriftDeparture', '/voyage/reports/drift-departure', null),
      leaf('voyageReportsCanalStrait', '/voyage/reports/canal-strait', null),
      leaf('voyageReportsNoon', '/voyage/reports/noon', null),
      offMenu('voyageLegs', '/voyage/legs', 'voyageReportsAll'),
      offMenu('voyageNoonReport', '/voyage/noon-report', 'voyageReportsNoon'),
    ]),
    subgroup('voyageRecords', [
      leaf('voyageList', '/voyage/list', null),
      leaf('voyageNew', '/voyage/new', null),
      leaf('voyageDetails', '/voyage/details', null),
      leaf('voyageTimeline', '/voyage/timeline', null),
      leaf('voyageSummary', '/voyage/summary', null),
      // Limanlar ve mesafeler (ADR 0048 §6; ortağın notu 3)
      leaf('voyageRoute', '/voyage/route', null),
    ]),
    subgroup('voyageAnalysis', [
      leaf('voyagePerformance', '/voyage/performance', null),
      // Charter party register (ADR 0084 §7): CP şartları seferden ayrı, ofis yazar
      leaf('voyageCharterParties', '/voyage/charter-parties', null),
      leaf('voyageCharterParty', '/voyage/charter-party', null),
      leaf('voyageCiiMrv', '/voyage/cii-mrv', 'phase3'),
      leaf('voyageFuelAnalysis', '/voyage/fuel-analysis', null),
    ]),
    subgroup('voyageReference', [
      leaf('voyageDistances', '/voyage/distances', null),
      leaf('voyageReportTemplates', '/voyage/report-templates', null),
      leaf('voyageEventTypes', '/voyage/event-types', null),
    ]),
  ]),
  group('crew', IconCrewFilled, [
    leaf('crewList', '/crew', null),
    // Crew List IMO FAL Form 5 (ortak 2026-10-26 madde 4; ADR 0130 §B): gemi ve ofis hazırlar
    leaf('crewLists', '/crew/lists', null),
    leaf('crewCertificates', '/crew/certificates', null),
    // Training needs (ADR 0128 Faz 4 Ek): tatbikat bulgusundan ya da elle; `drills` izniyle
    leaf('crewTraining', '/crew/training', null),
    leaf('crewSignOnOff', '/crew/sign-on-off', null),
    // Giriş hesapları (sahip 2026-11-01; ADR 0147): hesaplar ofiste yönetilir, gemide yok
    leaf('crewLoginAccounts', '/crew/login-accounts', null, 'office'),
    leaf('crewCalendar', '/crew/calendar', 'phase2'),
    leaf('crewRestHours', '/crew/rest-hours', null),
    // Gemi saati kaydı (M18; ADR 0065): saat değişikliği, kaptan onayı; ofiste salt okunur
    leaf('crewShipClock', '/crew/ship-clock', null),
    // Mürettebat bordrosu (M16; ADR 0061 §7): ortağın Payroll menüsü, sade. Yalnız `payroll.view`
    // yetkisi olan görür. Faz 2 yer tutucuları (Leave Management, Final Settlement) kaldırıldı
    // (M16b, ADR 0066)
    {
      ...subgroup('crewPayroll', [
        leaf('crewPayrollMonthly', '/crew/payroll', null),
        leaf('crewPayrollAdvances', '/crew/payroll/advances', null),
        leaf('crewPayrollAllotments', '/crew/payroll/allotments', null),
        leaf('crewPayrollPayslips', '/crew/payroll/payslips', null),
        leaf('crewPayrollPayments', '/crew/payroll/payments', null),
        leaf('crewPayrollClosing', '/crew/payroll/closing', null),
        leaf('crewPayrollReports', '/crew/payroll/reports', null),
      ]),
      permission: 'payroll',
    },
  ]),
  group('operations', IconSteeringWheel, [
    // Günlük iş planı ve günlük raporlar (M13e; ADR 0051)
    leaf('operationsDailyPlan', '/operations/daily-plan', null),
    leaf('operationsDailyReports', '/operations/daily-reports', null),
    // Liman aramaları (M13f; ADR 0052)
    leaf('operationsPortCalls', '/operations/port-calls', null),
    // Tatbikat yönetimi (ADR 0128 §6): Faz 1 Library, Faz 2 Dashboard ve Planner, Faz 3 Records
    subgroup('operationsDrills', [
      leaf('operationsDrillsDashboard', '/operations/drills/dashboard', null),
      leaf('operationsDrillsPlanner', '/operations/drills/planner', null),
      leaf('operationsDrillsRecords', '/operations/drills/records', null),
      leaf('operationsDrillsCompetency', '/operations/drills/competency', null),
      leaf('operationsDrillsAnalytics', '/operations/drills/analytics', null, 'office'),
      leaf('operationsDrillsLibrary', '/operations/drills/library', null),
    ]),
  ]),
  group('compliance', IconClipboardCheck, [
    // ISM Forms üçüncü düzey grup ve Manuals (ADR 0023; menu-yapisi.md; plan M8a Task 13)
    subgroup('complianceIsmForms', [
      leaf('complianceIsmFormList', '/compliance/ism-forms', null),
      leaf('complianceIsmFormCategories', '/compliance/ism-forms/categories', null),
      // Kayıt listesi (ADR 0042 §21; M8b3); kayıt sayfası menüde yoktur, sayfa içi açılır
      leaf('complianceIsmFormHistory', '/compliance/ism-forms/history', null),
    ]),
    leaf('complianceManuals', '/compliance/manuals', null),
    // M7d: gemi sertifikaları, sörveyler ve klas (plan M7 Task 27a)
    leaf('complianceVesselCertificates', '/compliance/vessel-certificates', null),
    // HSEQ Reports (ortak 2026-10-20b H1; ADR 0097 §1): dört tür tek listede. Alt grup (ortak
    // 2026-10-25, sahip onayı; ADR 0097 Ek): All Reports ve türler; tür öğesi aynı listeyi o türle
    // süzülü açar, "New …" o türle başlar (sefer raporlarının kalıbı, ADR 0082)
    subgroup('complianceHseqReports', [
      leaf('complianceHseq', '/compliance/hseq', null),
      leaf('complianceNonConformity', '/compliance/non-conformity', null),
      leaf('complianceNearMiss', '/compliance/near-miss', null),
      leaf('complianceDeficiency', '/compliance/deficiency', null),
      leaf('complianceAccident', '/compliance/accident', null),
    ]),
    // Risk değerlendirmesi (M13d; ADR 0050), şirket kütüphanesi (K3; ADR 0087), gemi register'ı
    // (K4; ADR 0088) ve ana verisi (K2; ADR 0086): alt grup; ortağın ağacının öğeleri hazır oldukça
    // açılır (README §6.1). Library ve Master Data gemide salt okunur; register'da okundu onayı
    // gemide verilir
    subgroup('complianceRisk', [
      // Modülün panosu (K7; ADR 0095): gemide geminin, ofiste filonun ya da seçilen geminin
      leaf('complianceRiskDashboard', '/compliance/risk-assessment/dashboard', null),
      leaf('complianceRiskAssessment', '/compliance/risk-assessment', null),
      leaf('complianceRiskLibrary', '/compliance/risk-assessment/library', null),
      leaf('complianceRiskRegister', '/compliance/risk-assessment/register', null),
      // Operasyonel doğrulama (K5; ADR 0089): gemide doğrulama ve yetki, ofiste salt okunur
      leaf('complianceRiskVerification', '/compliance/risk-assessment/verification', null),
      // Bulgular ve düzeltici faaliyetler (K6; ADR 0094): gemide yazılır, ofiste karar
      leaf('complianceRiskFindings', '/compliance/risk-assessment/findings', null),
      // Monitoring & Reports (K7; ADR 0095): kütüphane durumu, register uyumu, doğrulama geçmişi
      leaf('complianceRiskReports', '/compliance/risk-assessment/reports', null),
      leaf('complianceRiskMasterData', '/compliance/risk-assessment/master-data', null),
    ]),
  ]),
  { id: 'materialMaster', path: '/material-master', icon: IconPackage, stage: null },
  group('purchasing', IconShoppingCart, [
    leaf('purchasingRequisitions', '/purchasing/requisitions', null),
    // RFQ ve teklif ofisin ticari verisidir, gemiye inmez (ADR 0026 §1)
    leaf('purchasingRfq', '/purchasing/rfq', null, 'office'),
    leaf('purchasingOrders', '/purchasing/orders', null),
    // Tedarikçi ofiste yönetilir; gemide izin listesiyle inen salt okunur dizin (ADR 0115)
    leaf('purchasingSuppliers', '/purchasing/suppliers', null),
    leaf('purchasingGrn', '/purchasing/grn', null),
    leaf('purchasingStock', '/purchasing/stock', null),
  ]),
  group('inventory', IconBuildingWarehouse, [
    // Spare Parts (ADR 0122 §5; ortağın isteği 2026-10-24): kumanya dışı malzemenin stoğu, eski
    // Stock In / Out ekranı; eski yol menüde görünmeden açılır
    leaf('inventorySpareParts', '/inventory/spare-parts', null),
    offMenu('inventoryStock', '/inventory/stock', 'inventorySpareParts'),
    // Kumanya (ADR 0099): lot, FEFO, son kullanma; ofiste salt okunur, kütüphane ofiste
    leaf('inventoryProvision', '/inventory/provision', null),
    // Aylık iaşe raporu ve victualling rate (ADR 0123): kumanya defterinden, salt okuma; yolu
    // Provision altında, menüde Provision'ın hemen altında
    leaf('inventoryVictualling', '/inventory/provision/victualling', null),
  ]),
  group('pms', IconTool, [
    // M7a: ekipman ağacı, kartı ve sayaçlar; diğer PMS ekranları M7b–M7e
    leaf('pmsEquipment', '/pms/equipment', null),
    leaf('pmsPlan', '/pms/plan', null),
    // Ertelemeler (ADR 0108 §4): onay gemide biter; ofis gemi ve filo listesinde izler
    leaf('pmsPostponements', '/pms/postponements', null),
    leaf('pmsWorkOrders', '/pms/work-orders', null),
    // Arıza bildirimi ve giderilmesi, DM-17 (M15e; ADR 0060): filo ve gemi listesi
    leaf('pmsDefects', '/pms/defects', null),
    leaf('pmsHistory', '/pms/history', null),
    // Dry dock ve Pre-Docking Inspection (ADR 0129 §9): Faz 1 Projects ve Library; Pre-Docking
    // Inspection ekranı Faz 2 (ADR 0129 Ek), Yard Job List Faz 3 (ADR 0129 Ek Faz 3). Kütüphane
    // ofiste yazılır, gemide salt okunur; denetim, bulgu ve iş gemide yazılır, ofiste salt okunur;
    // iş kararı ofiste
    subgroup('pmsDrydock', [
      leaf('pmsDrydockProjects', '/pms/drydock/projects', null),
      // Filo görünümü yalnız ofiste (ADR 0129 Ek Faz 5)
      leaf('pmsDrydockFleet', '/pms/drydock/fleet', null, 'office'),
      leaf('pmsDrydockInspection', '/pms/drydock/inspection', null),
      leaf('pmsDrydockJobs', '/pms/drydock/jobs', null),
      leaf('pmsDrydockLibrary', '/pms/drydock/library', null),
    ]),
    // Şirket PMS kütüphanesi (M15f; ADR 0063 §10): ofiste; gemide talep Equipment List'te
    leaf('pmsLibrary', '/pms/library', null, 'office'),
    // DNV MAD / Class Integration (ortak 2026-10-28; ADR 0132): ofiste; gemide iş kaydı formunun
    // iki isteğe bağlı alanı (bakım öncesi durum, adam-saat)
    leaf('pmsClassIntegration', '/pms/class-integration', null, 'office'),
  ]),
  // Yakıt Yönetimi (ADR 0117 §14; ortağın tasarımı 01–07): tek öğe, yedi sekme `/fuel/<sekme>`.
  // "Fuel & Energy ▸ Fuel & Emissions" (Faz 3 yer tutucusu) kalktı
  { id: 'fuelManagement', path: '/fuel', icon: IconGasStation, stage: null, nested: true },
  // Finance & Cash Management (ADR 0093 §8; ortağın kasa.pdf §1): kaptan kasası ve Cash to Master.
  // Yalnız `cash.view` yetkisi olan görür (finans verisi)
  {
    ...group('finance', IconCash, [
      // Gemi ve ofis kasaları (ADR 0110 §3): sahip "finansta önce kasaları görmek" istedi
      leaf('financeCashAccounts', '/finance/accounts', null),
      subgroup('financeCtm', [
        leaf('financeCtmRequests', '/finance/ctm', null),
        leaf('financeCtmApprovals', '/finance/ctm/approvals', null),
        leaf('financeCtmReceipts', '/finance/ctm/receipts', null),
        leaf('financeCtmHistory', '/finance/ctm/history', null),
      ]),
      subgroup('financeCash', [
        leaf('financeCashTransactions', '/finance/cash', null),
        leaf('financeCashBalances', '/finance/cash/balances', null),
        leaf('financeCashReconciliation', '/finance/cash/reconciliation', null),
        leaf('financeCashMonthly', '/finance/cash/monthly', null),
      ]),
      leaf('financeReports', '/finance/reports', null),
    ]),
    permission: 'cash',
  },
  { id: 'reports', path: '/reports', icon: IconFileText, stage: null },
  { id: 'settings', path: '/settings', icon: IconSettingsFilled, stage: null },
];

/** Bütün öğeler (gruplar, alt gruplar ve alt öğeler), menü sırasıyla. */
export function allMenuItems(menu: readonly MenuItem[] = MENU): MenuItem[] {
  return menu.flatMap((item) => [item, ...allMenuItems(item.children ?? [])]);
}

/**
 * Sayfa açan öğeler (yapraklar) ve ana grupları: uygulamaların yolları buradan kurulur. Grubun
 * yolu ilk yaprağının yoludur; ikinci kez yol olmaz. `group` ana gruptur (ikon için). Menüde
 * görünmeyen yollar (`offMenu`) da yapraktır: sayfaları açılır (ADR 0082).
 */
export function menuLeaves(
  menu: readonly MenuItem[] = MENU,
): { item: MenuItem; group?: MenuItem }[] {
  return menu.flatMap((item) =>
    item.children
      ? allMenuItems(item.children)
          .filter((child) => !child.children)
          .map((child) => ({ item: child, group: item }))
      : [{ item }],
  );
}

/**
 * Öğenin sayfasını açmak için görme izni gereken modüller (ADR 0067 §4): kişi bunlardan hiçbirini
 * göremiyorsa öğe gizlenir. Anahtar öğe kimliği; `ship` gemi uygulamasında farklıysa (`null`:
 * gemide kısıtsız). Modüller sayfanın ana okuma ucunun istediği izindir (ofis ve gemi API'si).
 */
export const MENU_VIEW_MODULES: Readonly<
  Record<string, { web: readonly Module[]; ship?: readonly Module[] | null }>
> = {
  // Pano kartları: personel belgeleri, dinlenme, PMS ve form onayları (ADR 0067 §5); bekleyen iş
  // doğuran izinler de panoyu açar (ADR 0156). Gemide pano kişinin kendi verisiyle her zaman açılır
  dashboard: {
    web: [
      'crew_certificates',
      'rest_hours',
      'maintenance_program',
      'work_orders',
      'ism_forms',
      'cash',
      'purchasing',
      'fuel',
      'vessel_certificates',
      'hse_reports',
      'risk_assessments',
      'documents',
      'defects',
      'postponements',
    ],
    ship: null,
  },
  // All Reports ve dokuz rapor öğesi: sefer okuma izni (ADR 0082)
  voyageReportsAll: { web: ['voyage'] },
  voyageReportsArrival: { web: ['voyage'] },
  voyageReportsDailyPort: { web: ['voyage'] },
  voyageReportsDeparture: { web: ['voyage'] },
  voyageReportsAnchoring: { web: ['voyage'] },
  voyageReportsAnchorAweigh: { web: ['voyage'] },
  voyageReportsDrifting: { web: ['voyage'] },
  voyageReportsDriftDeparture: { web: ['voyage'] },
  voyageReportsCanalStrait: { web: ['voyage'] },
  voyageReportsNoon: { web: ['voyage'] },
  voyageList: { web: ['voyage'] },
  voyageNew: { web: ['voyage'] },
  voyageLegs: { web: ['voyage'] },
  voyageRoute: { web: ['voyage'] },
  voyageDistances: { web: ['voyage'] },
  voyageNoonReport: { web: ['voyage'] },
  voyagePerformance: { web: ['voyage'] },
  voyageCharterParty: { web: ['voyage'] },
  voyageCharterParties: { web: ['voyage'] },
  // Sefer kaydı, çizelge, özet ve Reference Data (ADR 0082 Ek): sefer okuma izni
  voyageDetails: { web: ['voyage'] },
  voyageTimeline: { web: ['voyage'] },
  voyageSummary: { web: ['voyage'] },
  voyageReportTemplates: { web: ['voyage'] },
  voyageEventTypes: { web: ['voyage'] },
  // Fuel & Consumption Analysis (V6; ADR 0090): sefer okuma izni
  voyageFuelAnalysis: { web: ['voyage'] },
  // Ofiste personel listesi `users.view`; gemide herkes en az kendini görür (ADR 0012 §5)
  crewList: { web: ['users'], ship: null },
  // Sign On / Off (ADR 0131 §5): ofiste personel görme; gemide personel belgelerini görme (kaptan
  // gemiyi, diğerleri kendini)
  crewSignOnOff: { web: ['users'], ship: ['crew_certificates'] },
  // Crew ▸ Login Accounts (ADR 0147): Settings ▸ Users ile aynı izin
  crewLoginAccounts: { web: ['users'] },
  crewCertificates: { web: ['crew_certificates'] },
  // Crew List (ADR 0130 §B5): personel belgelerini görme izni (kişisel veri)
  crewLists: { web: ['crew_certificates'] },
  crewRestHours: { web: ['rest_hours'] },
  crewShipClock: { web: ['ship_clock'] },
  operationsDailyPlan: { web: ['daily_works'] },
  operationsDailyReports: { web: ['daily_reports'] },
  operationsPortCalls: { web: ['voyage'] },
  // Tatbikat kütüphanesi (ADR 0128 §5): ofiste ve gemide `drills.view`
  operationsDrillsDashboard: { web: ['drills'] },
  operationsDrillsPlanner: { web: ['drills'] },
  operationsDrillsRecords: { web: ['drills'] },
  operationsDrillsLibrary: { web: ['drills'] },
  // Crew Competency Matrix ve Training needs (Faz 4 Ek): geçici olarak `drills` izni (#1806)
  operationsDrillsCompetency: { web: ['drills'] },
  // Filo analitiği (Faz 5 Ek): yalnız ofis, `drills.view`
  operationsDrillsAnalytics: { web: ['drills'] },
  crewTraining: { web: ['drills'] },
  // ISM form listesi ve kategorileri doküman kütüphanesidir (ADR 0023); kayıtlar ism_forms
  complianceIsmFormList: { web: ['documents'] },
  complianceIsmFormCategories: { web: ['documents'] },
  complianceIsmFormHistory: { web: ['ism_forms'] },
  complianceManuals: { web: ['documents'] },
  complianceVesselCertificates: { web: ['vessel_certificates'] },
  complianceHseq: { web: ['hse_reports'] },
  complianceDeficiency: { web: ['hse_reports'] },
  // Ofisin şirket uygunsuzluğu da bu ekrandadır (ADR 0062 §2)
  complianceNonConformity: { web: ['hse_reports', 'company_nonconformities'] },
  complianceAccident: { web: ['hse_reports'] },
  complianceNearMiss: { web: ['hse_reports'] },
  complianceRiskAssessment: { web: ['risk_assessments'] },
  // Kütüphane ofiste `ra_library`; gemide yayımlı sürüm gemi RA'sının görme izniyle (ADR 0089 §5)
  complianceRiskLibrary: { web: ['ra_library'], ship: ['risk_assessments'] },
  complianceRiskRegister: { web: ['risk_assessments'] },
  complianceRiskVerification: { web: ['ra_operations'] },
  complianceRiskFindings: { web: ['ra_operations'] },
  // Pano ve raporlar register, doğrulama ya da kütüphane izinlerinden biriyle (ADR 0095 §1)
  complianceRiskDashboard: {
    web: ['risk_assessments', 'ra_operations', 'ra_library'],
    ship: ['risk_assessments', 'ra_operations'],
  },
  complianceRiskReports: {
    web: ['risk_assessments', 'ra_operations', 'ra_library'],
    ship: ['risk_assessments', 'ra_operations'],
  },
  complianceRiskMasterData: { web: ['ra_library'], ship: ['risk_assessments'] },
  materialMaster: { web: ['materials'] },
  purchasingRequisitions: { web: ['purchasing'] },
  purchasingRfq: { web: ['purchasing'] },
  purchasingOrders: { web: ['purchasing'] },
  purchasingSuppliers: { web: ['purchasing'] },
  purchasingGrn: { web: ['purchasing', 'stock'] },
  purchasingStock: { web: ['stock'] },
  inventorySpareParts: { web: ['stock'] },
  inventoryStock: { web: ['stock'] },
  // Kumanya kendi yetki modülünde (ADR 0099 §4)
  inventoryProvision: { web: ['provisions'] },
  inventoryVictualling: { web: ['provisions'] },
  pmsEquipment: { web: ['equipment'] },
  pmsPlan: { web: ['maintenance_program'] },
  pmsPostponements: { web: ['postponements', 'maintenance_program'] },
  pmsWorkOrders: { web: ['work_orders'] },
  pmsDefects: { web: ['defects'] },
  pmsHistory: { web: ['work_records'] },
  pmsLibrary: { web: ['equipment'] },
  // Klas bilgisi sertifika modülünde (Class Items ile aynı; ADR 0132 §6)
  pmsClassIntegration: { web: ['vessel_certificates', 'settings'] },
  // Dry dock (ADR 0129 §8): ofiste ve gemide `drydock.view`
  pmsDrydockProjects: { web: ['drydock'] },
  pmsDrydockFleet: { web: ['drydock'] },
  pmsDrydockInspection: { web: ['drydock'] },
  pmsDrydockJobs: { web: ['drydock'] },
  pmsDrydockLibrary: { web: ['drydock'] },
  // Yakıt yönetimi (ADR 0117 §12)
  fuelManagement: { web: ['fuel'] },
};

/**
 * Görme iznine bağlanmayan hazır öğeler, gerekçesiyle (ADR 0067 §4). Yeni öğe ya
 * `MENU_VIEW_MODULES`'a ya da buraya girer (`menu.test.ts`).
 */
export const MENU_UNGATED: Readonly<Record<string, string>> = {
  fleetVessels: 'Gemi listesi şirket rolü olan herkese açıktır (ofis /vessels)',
  reports: 'Rapor sayfası her raporu kendi modülünün izniyle açar; sayfa kendisi kısıtsız',
  settings: 'Ayarlar kartları kendi izinleriyle görünür (marka, kullanıcılar, cihazlar, onaylar)',
};

/** Seçenekler: `complete` görülebilen modüller tam liste mi; `scope` web ofis ya da gemi. */
export interface HiddenMenuOptions {
  /**
   * Oturum bilgisi görülebilen bütün modülleri veriyor mu (ADR 0067 §4). Değilse (eski gemi
   * sunucusu yalnız kısıtlı modülleri verir) yalnız kısıtlı modüller (`permission`) gizlenir.
   */
  complete?: boolean;
  scope?: MenuScope;
}

/**
 * Kişinin göremediği menü öğeleri: kısıtlı modüller (ADR 0061 §6) ve tam listede görme izni
 * olmayan sayfalar (ADR 0067 §4). `viewable` oturum bilgisinden gelir; verilmezse (eski sunucu)
 * hiçbir kısıtlı modül görünmez. Boşalan grup `SideNav`'da görünmez.
 */
export function hiddenMenuItems(
  viewable: readonly string[] | undefined,
  options: HiddenMenuOptions = {},
  menu: readonly MenuItem[] = MENU,
): Set<string> {
  const allowed = new Set(viewable ?? []);
  const gate = (item: MenuItem): readonly Module[] | null => {
    const entry = MENU_VIEW_MODULES[item.id];
    if (!entry) return null;
    if (options.scope === 'ship' && entry.ship !== undefined) return entry.ship;
    return entry.web;
  };
  return new Set(
    allMenuItems(menu)
      .filter((item) => {
        if (item.permission !== undefined && !allowed.has(item.permission)) return true;
        if (!options.complete || viewable === undefined) return false;
        const modules = gate(item);
        return modules !== null && !modules.some((module) => allowed.has(module));
      })
      .map((item) => item.id),
  );
}

/**
 * Girişten sonra açılacak sayfa (ADR 0067 §5): pano görünüyorsa pano, değilse menü sırasıyla ilk
 * görünen hazır sayfa; hiçbiri yoksa pano (boş durum).
 */
export function firstVisiblePath(
  hidden: ReadonlySet<string>,
  scope: MenuScope = 'web',
  menu: readonly MenuItem[] = MENU,
): string {
  const visible = (items: readonly MenuItem[]): string | null => {
    for (const item of items) {
      if (hidden.has(item.id) || item.offMenu) continue;
      if (item.children) {
        const found = visible(item.children);
        if (found) return found;
      } else if (menuStage(item, scope) === null) {
        return item.path;
      }
    }
    return null;
  };
  return visible(menu) ?? '/dashboard';
}

/** Bu uygulamada öğenin aşaması; `null` hazır demektir. */
export function menuStage(item: MenuItem, scope: MenuScope): MenuStage | null {
  if (scope === 'ship' && item.shipStage !== undefined) return item.shipStage;
  return item.stage;
}

function matches(path: string, itemPath: string): boolean {
  return path === itemPath || path.startsWith(`${itemPath}/`);
}

/**
 * Yolun menüdeki izi: `[grup, alt öğe]` ya da `[ana öğe]`; bulunamazsa boş. Alt yollar
 * (`/fleet/<id>`) en uzun önekle eşleşir. Breadcrumb ve aktif öğe buradan.
 */
export function findMenuTrail(path: string, menu: readonly MenuItem[] = MENU): MenuItem[] {
  let best: MenuItem[] = [];
  let bestLength = -1;
  const visit = (items: readonly MenuItem[], parents: MenuItem[]) => {
    for (const item of items) {
      const trail = [...parents, item];
      if (item.children) {
        visit(item.children, trail);
      } else if (matches(path, item.path) && item.path.length > bestLength) {
        best = trail;
        bestLength = item.path.length;
      }
    }
  };
  visit(menu, []);
  return best;
}

/** Sol menünün açık grupları ve hangi yolda açıldıkları. */
export interface MenuOpenState {
  path: string;
  open: ReadonlySet<string>;
}

/** Etkin yaprağın bütün grupları (ana grup ve üçüncü düzeyde alt grup). */
function activeGroupsOf(path: string, menu: readonly MenuItem[]): string[] {
  return findMenuTrail(path, menu)
    .slice(0, -1)
    .map((item) => item.id);
}

/**
 * Açık menü grupları (M6f G1-14): yol her değiştiğinde yalnız etkin grup (üçüncü düzeyde alt grubu
 * da) açık kalır; üst düzey
 * sayfada (Dashboard, Settings) hiçbiri. Aynı sayfada elle açılan grup o sayfadan çıkılana kadar
 * açık durur. Saf işlev; `SideNav` durumu bununla tutar.
 */
export const menuOpenGroups = {
  initial(path: string, menu: readonly MenuItem[] = MENU): MenuOpenState {
    return { path, open: new Set(activeGroupsOf(path, menu)) };
  },
  navigate(state: MenuOpenState, path: string, menu: readonly MenuItem[] = MENU): MenuOpenState {
    return path === state.path ? state : menuOpenGroups.initial(path, menu);
  },
  toggle(state: MenuOpenState, id: string): MenuOpenState {
    const open = new Set(state.open);
    if (open.has(id)) open.delete(id);
    else open.add(id);
    return { path: state.path, open };
  },
};
